import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { siteConfig, homeContent } from '../data/site-content';
import { ChevronRight, CheckCircle2, MessageSquare, ArrowRight, Star, Play, X } from 'lucide-react';
import { motion } from 'framer-motion';
import TestimonialCarousel from '../components/TestimonialCarousel';

const Home = () => {
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/videos');
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            // Sort videos so "første" comes first
            const sorted = [...data].sort((a, b) => {
              const titleA = (a.title?.da || '').toLowerCase();
              const titleB = (b.title?.da || '').toLowerCase();
              if (titleA.includes('første') && !titleB.includes('første')) return -1;
              if (!titleA.includes('første') && titleB.includes('første')) return 1;
              return 0;
            });
            setVideos(sorted);
          } else {
            // Fallback to static video placeholders
            setVideos([
              {
                id: 'v1',
                title: { da: 'Klientens oplevelse af første behandling' },
                description: { da: 'Se en klients ærlige oplevelse af første behandling hos Corehypnose.' },
                thumbnail: ['https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/61716d4d-6634-47d6-abf0-b43bb340aac1.jpg'],
                video_url: '#'
              },
              {
                id: 'v2',
                title: { da: 'Klientens 8. og sidste behandling' },
                description: { da: 'Hør om resultaterne efter 8. og sidste behandling i et metakognitivt forløb.' },
                thumbnail: ['https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/61716d4d-6634-47d6-abf0-b43bb340aac1.jpg'],
                video_url: '#'
              }
            ]);
          }
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoadingVideos(false);
      }
    };

    fetchVideos();
  }, []);
  return (
    <>
      <Helmet>
        <title>Hypnose København | Hypnotisør København</title>
        <meta name="description" content="Leder du efter en Hypnotisør I København?. Opdag sindets kraft med Hypnose. Behandling af angst, stress, depression, m.m. 25 års erfaring" />
        <meta property="og:title" content="Hypnose København | Hypnotisør København" />
        <meta property="og:description" content="Leder du efter en Hypnotisør I København?. Opdag sindets kraft med Hypnose. Behandling af angst, stress, depression, m.m. 25 års erfaring" />
        <meta property="og:image" content="" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[#F4EFE7]/50 -z-10" />
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#123F13]/10 text-[#123F13] text-xs font-bold uppercase tracking-widest mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#123F13] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#123F13]"></span>
                </span>
                Metakognitiv Terapi & Hypnose
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#123F13] leading-[1.1] mb-8">
                {homeContent.hero.title}
              </h1>
              <p className="text-xl md:text-2xl text-[#8C2F12] font-medium mb-2">
                {homeContent.hero.subtitle}
              </p>
              <p className="text-lg font-serif font-bold text-[#123F13] mb-6">
                v/ Lars Nissen Corell – Psykoterapeut MPF
              </p>
              <p className="text-lg text-[#3B352F]/80 leading-relaxed mb-10 max-w-xl">
                {homeContent.hero.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/bestil-tid" 
                  className="bg-[#123F13] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#123F13]/90 transition-all flex items-center gap-2 shadow-lg shadow-[#123F13]/20"
                >
                  Book første session <ArrowRight size={18} />
                </Link>
                <Link 
                  to="/metakognitiv-terapi" 
                  className="bg-white border border-[#E8DDCE] text-[#3B352F] px-8 py-4 rounded-lg font-semibold hover:bg-[#FAF7F2] transition-all"
                >
                  Læs mere om metoden
                </Link>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl bg-[#E8DDCE] overflow-hidden shadow-2xl relative">
                <img 
                  src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/287004c7-3316-4207-8703-2728eb5ffba5.png" 
                  alt="Lars Nissen Corell – Psykoterapeut MPF" 
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#123F13]/20 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-[#FAF7F2] hidden md:block">
                <div className="flex items-center gap-1 text-[#B89B5E] mb-2">
                  {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <p className="text-sm font-bold text-[#123F13]">Over 26 års erfaring</p>
                <p className="text-xs text-[#3B352F]/60">Specialist i angst & stress</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Recognition Section */}
      <section className="py-7 md:py-9 bg-white border-y border-[#E8DDCE] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Mobile & Tablet layout (< lg): 2-kolonner eller elegant struktureret grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-3 max-w-lg sm:max-w-2xl mx-auto">
            <div className="text-[11px] sm:text-xs font-bold tracking-wider text-[#3B352F] flex items-center gap-2.5 uppercase bg-[#FAF7F2] border border-[#E8DDCE]/60 rounded-lg px-3.5 py-2.5 shadow-2xs">
              <CheckCircle2 className="w-4 h-4 text-[#123F13] shrink-0" />
              <span>Psykoterapeut MPF</span>
            </div>
            <div className="text-[11px] sm:text-xs font-bold tracking-wider text-[#3B352F] flex items-center gap-2.5 uppercase bg-[#FAF7F2] border border-[#E8DDCE]/60 rounded-lg px-3.5 py-2.5 shadow-2xs">
              <CheckCircle2 className="w-4 h-4 text-[#123F13] shrink-0" />
              <span>Metakognitiv Terapi & Hypnose</span>
            </div>
            <div className="text-[11px] sm:text-xs font-bold tracking-wider text-[#3B352F] flex items-center gap-2.5 uppercase bg-[#FAF7F2] border border-[#E8DDCE]/60 rounded-lg px-3.5 py-2.5 shadow-2xs">
              <CheckCircle2 className="w-4 h-4 text-[#123F13] shrink-0" />
              <span>Cert. Metakognitiv Psykoterapeut (Level IV)</span>
            </div>
            <div className="text-[11px] sm:text-xs font-bold tracking-wider text-[#3B352F] flex items-center gap-2.5 uppercase bg-[#FAF7F2] border border-[#E8DDCE]/60 rounded-lg px-3.5 py-2.5 shadow-2xs">
              <CheckCircle2 className="w-4 h-4 text-[#123F13] shrink-0" />
              <span>Certificeret Supervisor & Hypnoterapeut</span>
            </div>
          </div>

          {/* PC / Desktop layout (>= lg): Bevares præcis på 1 ubrudt række */}
          <div className="hidden lg:flex lg:flex-nowrap items-center justify-center 2xl:justify-between gap-6 lg:gap-8 opacity-85 grayscale hover:grayscale-0 transition-all duration-500 py-1">
             <div className="text-xs font-bold tracking-wider text-[#3B352F] flex items-center gap-2 uppercase shrink-0 whitespace-nowrap">
               <CheckCircle2 className="w-4 h-4 text-[#123F13] shrink-0" /> Psykoterapeut MPF
             </div>
             <div className="text-xs font-bold tracking-wider text-[#3B352F] flex items-center gap-2 uppercase shrink-0 whitespace-nowrap">
               <CheckCircle2 className="w-4 h-4 text-[#123F13] shrink-0" /> Certificeret Metakognitiv Psykoterapeut (Level IV)
             </div>
             <div className="text-xs font-bold tracking-wider text-[#3B352F] flex items-center gap-2 uppercase shrink-0 whitespace-nowrap">
               <CheckCircle2 className="w-4 h-4 text-[#123F13] shrink-0" /> Certificeret Supervisor & Hypnoterapeut
             </div>
             <div className="text-xs font-bold tracking-wider text-[#3B352F] flex items-center gap-2 uppercase shrink-0 whitespace-nowrap">
               <CheckCircle2 className="w-4 h-4 text-[#123F13] shrink-0" /> Metakognitiv Terapi & Hypnose
             </div>
          </div>
        </div>
      </section>

      {/* Video Reviews Section */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="inline-block px-4 py-1.5 mb-6 text-[11px] font-bold tracking-[0.2em] uppercase text-[#8C2F12] bg-[#8C2F12]/5">
              Video-anmeldelser
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">Hør klienternes egne ord</h2>
            <p className="text-[#3B352F]/70">Oplev hvordan metakognitiv terapi har gjort en forskel for andre.</p>
          </div>

          {loadingVideos ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#123F13]"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {videos.map((video) => (
                <div 
                  key={video.id} 
                  className="group cursor-pointer"
                  onClick={() => setActiveVideo(video)}
                >
                  <div className="relative aspect-video bg-[#E8DDCE] overflow-hidden rounded-xl mb-4 shadow-sm">
                    <img 
                      src={video.thumbnail?.[0]} 
                      alt={video.title.da} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-102"
                    />
                    <div className="absolute inset-0 bg-black/10 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                      <div className="w-12 h-12 bg-white/90 backdrop-blur-xs rounded-full flex items-center justify-center shadow-md transform group-hover:scale-105 transition-transform">
                        <Play className="w-5 h-5 text-[#123F13] fill-[#123F13] ml-0.5" />
                      </div>
                    </div>
                  </div>
                  <h3 className="text-lg font-serif font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors px-1">
                    {video.title.da}
                  </h3>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialCarousel />

      {/* Pain Points / Services Section */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <img 
              src="/logo 2/imgi_24_Certifikat-png1080x1080.png" 
              alt="Certifikat Logo" 
              className="h-24 mx-auto mb-8 object-contain"
            />
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight">
              {homeContent.sections[0].title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {homeContent.sections[0].items.map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border border-[#E8DDCE] hover:shadow-xl transition-all group">
                <div className="w-12 h-12 rounded-lg bg-[#F4EFE7] flex items-center justify-center text-[#123F13] mb-6 group-hover:bg-[#123F13] group-hover:text-white transition-colors">
                  <CheckCircle2 size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#123F13] mb-4">{item.title}</h3>
                <p className="text-[#3B352F]/70 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <Link 
              to="/behandlinger" 
              className="bg-[#123F13] text-white px-10 py-4 rounded-lg font-bold hover:bg-[#123F13]/90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#123F13]/10"
            >
              Se alle behandlinger <ArrowRight size={18} />
            </Link>
            <Link 
              to="/kontakt" 
              className="bg-white border border-[#E8DDCE] text-[#123F13] px-10 py-4 rounded-lg font-bold hover:bg-[#FAF7F2] transition-all flex items-center justify-center gap-2"
            >
              Kontakt mig <MessageSquare size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Parterapi Highlight Section */}
      <section className="py-20 bg-white border-b border-[#E8DDCE]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-3xl p-8 md:p-12 lg:p-16 relative overflow-hidden shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              
              {/* Text Content */}
              <div className="lg:col-span-7">
                <span className="flex items-center gap-3 text-[#8C2F12] font-bold text-xs uppercase tracking-widest mb-6">
                  <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                  Parterapi og relationscoaching
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6 leading-tight">
                  Når I vil forstå hinanden igen
                </h2>
                <p className="text-lg text-[#3B352F]/80 leading-relaxed mb-8">
                  For dig eller jer, der ønsker hjælp til kommunikation, konflikter, afstand eller gentagne mønstre i relationen. Du kan komme alene eller sammen med din partner.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/parterapi" 
                    className="bg-[#123F13] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#123F13]/90 transition-all flex items-center gap-2 shadow-lg shadow-[#123F13]/10"
                  >
                    Læs mere om parterapi <ArrowRight size={18} />
                  </Link>
                  <Link 
                    to="/bestil-tid" 
                    className="bg-white border border-[#E8DDCE] text-[#3B352F] px-8 py-4 rounded-lg font-semibold hover:bg-[#FAF7F2] transition-all"
                  >
                    Book første relationssession
                  </Link>
                </div>
              </div>

              {/* Image / Graphic */}
              <div className="lg:col-span-5">
                <div className="rounded-2xl overflow-hidden shadow-lg border border-[#E8DDCE]/60 bg-[#FAF7F2]/50">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/parterapi-og-relations-coaching.png" 
                    alt="Parterapi og relationscoaching i Valby" 
                    className="w-full h-auto block"
                  />
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative z-10 bg-[#FAF7F2] p-6 sm:p-8 md:p-12 rounded-2xl border border-[#E8DDCE]">
                <MessageSquare className="text-[#8C2F12] w-8 h-8 md:w-10 md:h-10 mb-5 md:mb-8" />
                <blockquote className="text-lg sm:text-xl md:text-2xl font-serif text-[#123F13] leading-relaxed mb-5 md:mb-8 italic">
                  "{homeContent.aboutPreview.quote}"
                </blockquote>
                <p className="text-base md:text-lg text-[#3B352F]/80 leading-relaxed mb-6 md:mb-8">
                  {homeContent.aboutPreview.description}
                </p>
                <div className="flex items-center gap-3.5 sm:gap-4">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 shrink-0 rounded-full bg-[#E8DDCE] overflow-hidden border border-[#E8DDCE]">
                    <img src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/007b2916-bfae-41b2-b260-6ef1d0f30181.jpg" alt="Lars" className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-[#123F13] text-sm sm:text-base leading-snug">Lars Nissen Corell – Psykoterapeut MPF</p>
                    <p className="text-xs sm:text-sm text-[#3B352F]/70 leading-snug mt-0.5">Certificeret metakognitiv psykoterapeut (Level IV) & hypnoterapeut</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#123F13]/5 rounded-full -z-10" />
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-[#8C2F12]/5 rounded-full -z-10" />
            </div>
            
            <div>
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-4">Erfaring & Tryghed</span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Et trygt rum i hjertet af Valby</h2>
              <p className="text-lg text-[#3B352F]/70 mb-8 leading-relaxed">
                Klinikken ligger centralt i Valby – 200 meter fra stationen. Nem adgang med tog og bus. Her starter din forandring i rolige omgivelser, hvor der er plads til dig.
              </p>
              <ul className="space-y-4 mb-10">
                {[
                  "Over 26 års erfaring",
                  "Psykoterapeut MPF",
                  "Certificeret metakognitiv psykoterapeut, Neokognitivt Institut, Level IV – højeste niveau",
                  "Certificeret metakognitiv supervisor og hypnoterapeut"
                ].map((point, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#123F13] font-medium">
                    <CheckCircle2 size={20} className="text-[#8C2F12]" />
                    {point}
                  </li>
                ))}
              </ul>

              {/* Certificeringsmærker (Guld på mørkegrøn) */}
              <div className="bg-[#123F13] text-white p-5 rounded-xl flex flex-col sm:flex-row items-center gap-5 my-8 shadow-sm max-w-lg">
                <div className="flex items-center gap-4 shrink-0">
                  <a
                    href="https://www.neokognitivtinstitut.dk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Neokognitivt Institut – Level IV Højeste Niveau"
                    className="w-20 h-20 shrink-0 transition-transform hover:scale-105"
                  >
                    <img
                      src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/level-4-guld-png-1080x1080-2-1.png"
                      alt="Neokognitivt Institut Level IV"
                      className="w-full h-full object-contain drop-shadow"
                    />
                  </a>
                  <a
                    href="https://psykoterapeutforeningen.dk/psykoterapeuter/91679-Lars-Nissen-Corell"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Dansk Psykoterapeutforening – Psykoterapeut MPF"
                    className="w-16 h-20 shrink-0 transition-transform hover:scale-105"
                  >
                    <img
                      src="/_assets/mpf-guld.png"
                      alt="Psykoterapeut MPF"
                      className="w-full h-full object-contain drop-shadow"
                    />
                  </a>
                </div>
                <div className="text-xs text-white/85 text-center sm:text-left">
                  <p className="font-serif font-bold text-[#f1dcab] text-sm">Dokumenteret faglighed</p>
                  <p className="text-[11px] text-white/70 mt-1 leading-relaxed">
                    Certificeret hos Neokognitivt Institut (Level IV) og medlem af Dansk Psykoterapeutforening (MPF).
                  </p>
                </div>
              </div>

              <Link 
                to="/om-lars" 
                className="inline-flex items-center gap-2 text-[#123F13] font-bold hover:gap-4 transition-all"
              >
                Læs mere om min baggrund <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Session Info Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Intro session i klinik og online</h2>
          <p className="text-lg text-[#3B352F]/70 mb-12">
            Sessionen giver dig mulighed for at mærke efter, om jeg er den rette terapeut for dig, og jeg lægger en klar plan for dit forløb. Sessionen inkluderer:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
            {[
              "En personlig kortlægning af dine udfordringer",
              "Guidede øvelser og praktiske teknikker til øjeblikkelig ro",
              "En klar, personlig handleplan for dine næste skridt",
              "Opfølgningsmateriale, der understøtter din udvikling"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-6 bg-[#FAF7F2] rounded-xl border border-[#E8DDCE]">
                <div className="w-6 h-6 rounded-full bg-[#123F13] text-white flex items-center justify-center shrink-0 mt-1">
                  <CheckCircle2 size={14} />
                </div>
                <p className="text-[#123F13] font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#123F13] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-[#8C2F12]/10 skew-x-12 transform translate-x-1/2" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="flex justify-center mb-6">
            <img 
              src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/corehypnose-lak-segl.png" 
              alt="Core Kvalitetsstempel" 
              className="w-24 h-24 rotate-[-6deg] drop-shadow-[0_10px_20px_rgba(0,0,0,0.3)] object-contain"
            />
          </div>
          <h2 className="text-3xl md:text-5xl font-serif mb-8 leading-tight">
            Klar til at få ro i tankerne?<br />
            <span className="text-[#E8DDCE]">Start med en enkelt session.</span>
          </h2>
          <p className="text-lg text-white/80 mb-12 max-w-2xl mx-auto">
            Første session koster kun 375 kr. Det giver mig mulighed for at se din situation an og lægge en plan, uden at du binder dig til noget.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/bestil-tid" 
              className="bg-white text-[#123F13] px-10 py-4 rounded-lg font-bold hover:bg-[#E8DDCE] transition-all"
            >
              Book første session
            </Link>
            <Link 
              to="/kontakt" 
              className="border border-white/30 text-white px-10 py-4 rounded-lg font-bold hover:bg-white/10 transition-all"
            >
              Kontakt mig
            </Link>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
          <button 
            onClick={() => setActiveVideo(null)}
            className="absolute top-6 right-6 text-white hover:text-[#B89B5E] transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="w-full max-w-5xl aspect-video bg-black shadow-2xl">
            <iframe
              src={`https://www.youtube.com/embed/${activeVideo.video_url.includes('v=') ? activeVideo.video_url.split('v=')[1].split('&')[0] : activeVideo.video_url.split('/').pop().split('?')[0]}?autoplay=1`}
              title={activeVideo.title.da}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </>
  );
};

export default Home;
